import { useState, useEffect } from "react";
import { useLocation } from "@/lib/router-compat";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Bell, Smartphone } from "lucide-react";
import { useTranslation } from "@/i18n/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// Only show on authenticated routes (after login)
const ALLOWED_ROUTES = ["/dashboard", "/enroll", "/account", "/schedule", "/admin", "/2fa-verify"];

const shouldShowOnRoute = (pathname: string): boolean => {
  return ALLOWED_ROUTES.some(route => pathname === route || pathname.startsWith(`${route}/`));
};

export const InstallPrompt = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { t } = useTranslation();

  const isAllowedRoute = shouldShowOnRoute(location.pathname);

  // Check auth state
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsLoggedIn(!!session);
    };
    checkAuth();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone === true;
    if (standalone) { setIsInstalled(true); return; }
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);
    const handleBeforeInstall = (e: Event) => { e.preventDefault(); setDeferredPrompt(e as BeforeInstallPromptEvent); };
    const handleAppInstalled = () => { setIsInstalled(true); setShowInstallDialog(false); setDeferredPrompt(null); };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", handleBeforeInstall); window.removeEventListener("appinstalled", handleAppInstalled); };
  }, []);

  useEffect(() => {
    if (!isAllowedRoute || isInstalled || !isMobile || !isLoggedIn) return;
    
    // Check if already shown today
    const lastShown = localStorage.getItem("pwa-install-last-shown");
    if (lastShown) {
      const today = new Date().toDateString();
      if (new Date(lastShown).toDateString() === today) return;
    }

    const canShow = isIOS || deferredPrompt !== null;
    if (!canShow) return;
    const timer = setTimeout(() => {
      setShowInstallDialog(true);
      localStorage.setItem("pwa-install-last-shown", new Date().toISOString());
    }, 2000);
    return () => clearTimeout(timer);
  }, [isAllowedRoute, isInstalled, deferredPrompt, isIOS, isMobile, isLoggedIn]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") { setIsInstalled(true); requestNotificationPermission(); }
    setDeferredPrompt(null);
    setShowInstallDialog(false);
  };

  const requestNotificationPermission = async () => {
    if ("Notification" in window && Notification.permission === "default") {
      try { const permission = await Notification.requestPermission(); if (permission === "granted") subscribeToPush(); } catch (error) { console.error("Error requesting notification permission:", error); }
    }
  };

  const subscribeToPush = async () => {
    try { if ('serviceWorker' in navigator && 'PushManager' in window) { await navigator.serviceWorker.ready; } } catch (error) { console.error("Failed to subscribe to push:", error); }
  };

  const handleDismiss = () => { setShowInstallDialog(false); };
  const handleNotificationsOnly = async () => { await requestNotificationPermission(); handleDismiss(); };
  const handleNotificationClose = (enable: boolean) => { setShowNotificationPrompt(false); if (enable) requestNotificationPermission(); };

  if (isInstalled) return null;

  return (
    <>
      <Dialog open={showInstallDialog} onOpenChange={setShowInstallDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Download className="h-6 w-6 text-primary" />
              {t('install.title')}
            </DialogTitle>
            <DialogDescription>{t('install.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {isIOS ? (
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                  <Smartphone className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-sm">{t('install.iosTitle')}</p>
                    <p className="text-sm text-muted-foreground">
                      1. Tik op het deel-icoon{' '}
                      <span className="inline-flex items-center justify-center w-6 h-6 border border-muted-foreground rounded align-middle mx-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                      </span>{' '}
                      onderaan het scherm
                      <br />
                      2. Kies <strong>'Zet op beginscherm'</strong>
                      <br />
                      <span className="text-xs text-muted-foreground">💡 Staat deze optie er niet bij? Scroll naar beneden of tik op <strong>'Toon meer'</strong> (^).</span>
                      <br />
                      3. Tik op <strong>'Voeg toe'</strong>
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                  <Smartphone className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-sm">{t('install.quickAccess')}</p>
                    <p className="text-sm text-muted-foreground">{t('install.quickAccessBody')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                  <Bell className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-sm">{t('install.pushNotifications')}</p>
                    <p className="text-sm text-muted-foreground">{t('install.pushNotificationsBody')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            {!isIOS && deferredPrompt && (
              <Button onClick={handleInstall} className="w-full gap-2"><Download className="h-4 w-4" />{t('install.install')}</Button>
            )}
            <Button variant="outline" onClick={handleNotificationsOnly} className="w-full gap-2"><Bell className="h-4 w-4" />{t('install.notificationsOnly')}</Button>
            <Button variant="ghost" onClick={handleDismiss} className="w-full">{t('common.later')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showNotificationPrompt} onOpenChange={setShowNotificationPrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell className="h-5 w-5 text-primary" />{t('install.enableNotifications')}</DialogTitle>
            <DialogDescription>{t('install.enableNotificationsBody')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
              <Bell className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-sm">{t('install.enableNotifications')}</p>
                <p className="text-sm text-muted-foreground">{t('install.enableNotificationsBody')}</p>
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button onClick={() => handleNotificationClose(true)} className="w-full gap-2"><Bell className="h-4 w-4" />{t('install.allowNotifications')}</Button>
            <Button variant="ghost" onClick={() => handleNotificationClose(false)} className="w-full">{t('install.notNow')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
